import { addDoc, collection, doc, getDocs, onSnapshot, orderBy, query, updateDoc, where } from 'firebase/firestore';
import { db } from '@/integrations/firebase/client';
import type { VideoSource } from '@/dao/types';

export type FeedbackStatus = 'draft' | 'running' | 'done' | 'error';

export interface FeedbackDoc {
  id: string;
  userId: string;
  name: string;
  video?: VideoSource | null;
  status: FeedbackStatus;
  archived?: boolean;
  createdAt: number; // epoch ms
  updatedAt?: number;
  runningTimestamp?: any;
  // Persisted UI state
  ageRange?: [number, number];
  regions?: string[];
  interests?: string[];
  selectedPersonas?: Array<{ id: string; name: string; age?: number }>;
  notes?: string;
  secondEditorOpinion?: boolean;
  language?: string;
  error?: string;
}

export type CreateFeedbackInput = Omit<FeedbackDoc, 'id' | 'createdAt'> & { createdAt?: number };

const COL = 'feedbacks';

export async function listFeedbacksByUser(userId: string, includeArchived = false): Promise<FeedbackDoc[]> {
  if (!userId) return [];
  const q = query(collection(db, COL), where('userId', '==', userId), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  const items: FeedbackDoc[] = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as any;
  return (includeArchived ? items : items.filter((f) => !f.archived)).sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));
}

export async function createFeedback(input: Partial<CreateFeedbackInput> & { userId: string }): Promise<FeedbackDoc> {
  const data: Omit<FeedbackDoc, 'id'> = {
    // Copy all fields from input first
    ...input,
    // Then override specific fields
    userId: input.userId,
    name: input.name ?? new Date().toLocaleString(),
    video: input.video ?? null,
    status: input.status ?? 'draft',
    archived: input.archived ?? false,
    createdAt: input.createdAt ?? Date.now(),
    updatedAt: Date.now(),
  } as any;
  
  // Remove undefined values as Firebase doesn't support them
  const cleanData = Object.fromEntries(
    Object.entries(data).filter(([_, value]) => value !== undefined)
  );
  
  const col = collection(db, COL);
  const ref = await addDoc(col, cleanData as any);
  return { id: ref.id, ...(data as any) } as FeedbackDoc;
}

export async function updateFeedback(id: string, patch: Partial<FeedbackDoc>): Promise<void> {
  const ref = doc(db, COL, id);
  await updateDoc(ref, { ...patch, updatedAt: Date.now() } as any);
}

export async function updateFeedbackName(id: string, name: string): Promise<void> {
  await updateFeedback(id, { name });
}

export function subscribeFeedbacksByUser(userId: string, cb: (items: FeedbackDoc[]) => void) {
  if (!userId) {
    cb([]);
    return () => {};
  }
  const q = query(collection(db, COL), where('userId', '==', userId), orderBy('createdAt', 'desc'));
  return onSnapshot(q, (snap) => {
    const items: FeedbackDoc[] = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as any;
    cb(items.sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0)));
  });
}
